import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { safeStorage } from "../utils/auth";

export interface AuthState {
  token: string | null;
  email: string | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  hasCompletedOnboarding: boolean;
  needsOnboarding: boolean;
  lastLogin: string | null;
  tier?: string;
  user?: {
    id?: string;
    email?: string;
    emailVerified?: boolean;
    createdAt?: string;
    tier?: string;
    isAdmin?: boolean;
    profile?: any;
  };
}

const initialState: AuthState = {
  token: null,
  email: null,
  isAuthenticated: false,
  isInitializing: true,
  hasCompletedOnboarding: false,
  needsOnboarding: false,
  lastLogin: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginSuccess(
      state,
      action: PayloadAction<{ token: string; email: string; hasCompletedOnboarding?: boolean; user?: any }>,
    ) {
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.isAuthenticated = true;
      state.isInitializing = false;
      state.hasCompletedOnboarding = action.payload.hasCompletedOnboarding || false;
      state.needsOnboarding = !action.payload.hasCompletedOnboarding;
      state.lastLogin = new Date().toISOString();
      state.user = action.payload.user;

      // Persist authentication data using safe storage
      safeStorage.setItem('token', action.payload.token);
      safeStorage.setItem('email', action.payload.email);
      safeStorage.setItem('hasCompletedOnboarding', String(action.payload.hasCompletedOnboarding || false));
      safeStorage.setItem('lastLogin', state.lastLogin);
    },

    logout(state) {
      state.token = null;
      state.email = null;
      state.isAuthenticated = false;
      state.isInitializing = false;
      state.hasCompletedOnboarding = false;
      state.needsOnboarding = false;
      state.user = undefined;

      // Clear all storage on logout
      safeStorage.clear();
    },

    initializationComplete(state) {
      state.isInitializing = false;
    },

    completeOnboarding(state) {
      state.hasCompletedOnboarding = true;
      state.needsOnboarding = false;
      // Persist onboarding completion
      safeStorage.setItem('hasCompletedOnboarding', 'true');
    },

    requireOnboarding(state) {
      state.needsOnboarding = true;
      state.hasCompletedOnboarding = false;
      // No localStorage persistence
    },

    refreshToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
      // No localStorage persistence
    },

    // Handle session expiration
    sessionExpired(state) {
      state.token = null;
      state.isAuthenticated = false;
      // Clear all auth data
    },

    setUser(state, action: PayloadAction<{ email: string; tier?: string }>) {
      state.email = action.payload.email;
      if (action.payload.tier) {
        state.tier = action.payload.tier;
      }
      // No localStorage persistence
    },

    updateEmailVerification(state, action: PayloadAction<boolean>) {
      if (state.user) {
        state.user.emailVerified = action.payload;
      }
      // No localStorage persistence
    },

    updateUserProfile(state, action: PayloadAction<Partial<AuthState['user']>>) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      } else {
        state.user = action.payload;
      }
    },
  },
});

export const { loginSuccess, logout, refreshToken, sessionExpired, setUser, initializationComplete, completeOnboarding, requireOnboarding, updateEmailVerification, updateUserProfile } =
  authSlice.actions;

// Selectors
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectEmail = (state: { auth: AuthState }) => state.auth.email;

export default authSlice.reducer;
